import { useEffect, useState } from "react";
import { format } from "date-fns";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { FuncionarioCombobox } from "@/components/FuncionarioCombobox";
import { Plus, Check, X, FilterX } from "lucide-react";
import { toast } from "sonner";
import { hm } from "@/features/ponto/pontoApi";

interface Ajuste {
  id: number; idFuncionario: number; funcionarioNome: string; funcionarioMatricula: string; dtReferencia: string;
  iTipoAjuste: number; fMinutosAjuste: number; sJustificativa: string; tipoTexto: string;
  iStatusAprovacao: number; statusTexto: string; dtHoraAjustada: string | null; dtHoraBatidaAfetada: string | null;
  dtAprovacao: string | null;
}
interface BatidaDia { id: number; dtHoraRegistro: string; tipoBatidaTexto: string; }

const TIPOS = [
  { v: 1, t: "Inclusão de batida" }, { v: 2, t: "Abono de falta" }, { v: 3, t: "Atestado" },
  { v: 4, t: "Ajuste banco de horas" }, { v: 5, t: "Desconsiderar batida" },
];
const TODOS = "__todos__";
const NENHUM_AJUSTE: Ajuste[] = [];
const hojeIso = format(new Date(), "yyyy-MM-dd");
const VAZIO = { IdFuncionario: 0, DtReferencia: hojeIso, ITipoAjuste: 3, FMinutosAjuste: 0, SJustificativa: "", Hora: "", IdPontoRegistroAfetado: 0 };
const dataBr = (s: string) => format(new Date(`${s.slice(0, 10)}T00:00:00`), "dd/MM/yyyy");
const hora = (s: string) => s.slice(11, 16);

/** Detalhe do ajuste na grade: hora incluída / batida desconsiderada / minutos. */
function detalhe(a: Ajuste) {
  if (a.iTipoAjuste === 1 && a.dtHoraAjustada) return `Incluir ${hora(a.dtHoraAjustada)}`;
  if (a.iTipoAjuste === 5 && a.dtHoraBatidaAfetada) return `Desconsiderar ${hora(a.dtHoraBatidaAfetada)}`;
  return a.fMinutosAjuste !== 0 ? hm(a.fMinutosAjuste) : "—";
}

export default function AjustesPage() {
  const qc = useQueryClient();
  const [aba, setAba] = useState<"pendentes" | "historico">("pendentes");
  const [funcionarioId, setFuncionarioId] = useState(0);
  const [tipo, setTipo] = useState(TODOS);
  const [status, setStatus] = useState(TODOS);
  const [dtIni, setDtIni] = useState("");
  const [dtFim, setDtFim] = useState("");
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO);

  const params = {
    status: aba === "pendentes" ? 0 : status === TODOS ? undefined : Number(status),
    funcionarioId: funcionarioId || undefined,
    tipo: tipo === TODOS ? undefined : Number(tipo),
    dtIni: dtIni || undefined,
    dtFim: dtFim || undefined,
  };
  const { data: itens = NENHUM_AJUSTE, isLoading } = useQuery({
    queryKey: ["ponto-ajustes", params],
    queryFn: () => apiClient.get<Ajuste[]>("/api/rh/ponto/ajustes", { params }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
  const pag = usePaginacaoLocal(itens);
  useEffect(() => setSel(new Set()), [itens]);

  // Batidas do dia (para escolher a que será desconsiderada).
  const { data: batidasDia = [], isFetching: carregandoBatidas } = useQuery({
    queryKey: ["ponto-batidas-dia", form.IdFuncionario, form.DtReferencia],
    queryFn: () => apiClient.get<BatidaDia[]>("/api/rh/ponto/batidas", {
      params: { funcionarioId: form.IdFuncionario, dia: form.DtReferencia },
    }).then((r) => r.data),
    enabled: dialog && form.ITipoAjuste === 5 && !!form.IdFuncionario && !!form.DtReferencia,
  });

  const filtrosAtivos = !!funcionarioId || tipo !== TODOS || status !== TODOS || !!dtIni || !!dtFim;
  const limparFiltros = () => { setFuncionarioId(0); setTipo(TODOS); setStatus(TODOS); setDtIni(""); setDtFim(""); };

  const pendentesVisiveis = pag.itensPaginados.filter((a) => a.iStatusAprovacao === 0);
  const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const todosDaPagina = pendentesVisiveis.length > 0 && pendentesVisiveis.every((a) => sel.has(a.id));
  const togglePagina = () => setSel((s) => {
    const n = new Set(s);
    if (todosDaPagina) pendentesVisiveis.forEach((a) => n.delete(a.id)); else pendentesVisiveis.forEach((a) => n.add(a.id));
    return n;
  });

  const recarregar = () => qc.invalidateQueries({ queryKey: ["ponto-ajustes"] });

  const decidir = async (aprovado: boolean) => {
    if (sel.size === 0) { toast.warning("Selecione ao menos um ajuste."); return; }
    try {
      const r = await apiClient.post("/api/rh/ponto/ajustes/aprovar", { IdsAjuste: [...sel], Aprovado: aprovado });
      toast.success(r.data?.mensagem ?? "Processado."); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao processar."); }
  };

  const salvarNovo = async () => {
    if (!form.IdFuncionario) { toast.warning("Selecione o funcionário."); return; }
    if (form.ITipoAjuste === 1 && !form.Hora) { toast.warning("Informe a hora da batida a incluir."); return; }
    if (form.ITipoAjuste === 5 && !form.IdPontoRegistroAfetado) { toast.warning("Escolha a batida a desconsiderar."); return; }
    try {
      await apiClient.post("/api/rh/ponto/ajustes", {
        IdFuncionario: form.IdFuncionario,
        DtReferencia: form.DtReferencia,
        ITipoAjuste: form.ITipoAjuste,
        FMinutosAjuste: [2, 3, 4].includes(form.ITipoAjuste) ? form.FMinutosAjuste : 0,
        SJustificativa: form.SJustificativa,
        DtHoraAjustada: form.ITipoAjuste === 1 ? `${form.DtReferencia}T${form.Hora}:00` : null,
        IdPontoRegistroAfetado: form.ITipoAjuste === 5 ? form.IdPontoRegistroAfetado : 0,
      });
      toast.success("Ajuste enviado para aprovação."); setDialog(false); setForm(VAZIO); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const statusBadge = (a: Ajuste) =>
    a.iStatusAprovacao === 1 ? <Badge className="bg-green-600 hover:bg-green-600">Aprovado</Badge>
      : a.iStatusAprovacao === 2 ? <Badge variant="destructive">Rejeitado</Badge>
        : <Badge variant="outline">Pendente</Badge>;

  const colunas = aba === "historico" ? 8 : 7;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Ajustes de Ponto</h1>
          <p className="text-sm text-muted-foreground">Justificativas (abono, atestado, inclusão/desconsideração de batida, banco de horas).</p>
        </div>
        <div className="flex gap-2">
          {sel.size > 0 && (
            <>
              <Button variant="outline" onClick={() => decidir(false)}><X className="h-4 w-4 mr-2" />Rejeitar ({sel.size})</Button>
              <Button onClick={() => decidir(true)}><Check className="h-4 w-4 mr-2" />Aprovar ({sel.size})</Button>
            </>
          )}
          <Button variant="secondary" onClick={() => { setForm(VAZIO); setDialog(true); }}><Plus className="h-4 w-4 mr-2" />Novo</Button>
        </div>
      </div>

      <Tabs value={aba} onValueChange={(v) => setAba(v as typeof aba)}>
        <TabsList>
          <TabsTrigger value="pendentes">Pendentes</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div className="space-y-1 lg:col-span-2">
          <Label>Funcionário</Label>
          <FuncionarioCombobox value={funcionarioId} onChange={setFuncionarioId} situacao="todos" placeholder="Todos" permitirLimpar />
        </div>
        <div className="space-y-1">
          <Label>Tipo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos</SelectItem>
              {TIPOS.map((t) => <SelectItem key={t.v} value={String(t.v)}>{t.t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>De</Label>
          <Input type="date" value={dtIni} onChange={(e) => setDtIni(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Até</Label>
          <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
        </div>
        {aba === "historico" && (
          <div className="space-y-1">
            <Label>Situação</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todas</SelectItem>
                <SelectItem value="0">Pendente</SelectItem>
                <SelectItem value="1">Aprovado</SelectItem>
                <SelectItem value="2">Rejeitado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {filtrosAtivos && <Button variant="ghost" size="sm" onClick={limparFiltros}><FilterX className="h-4 w-4 mr-2" />Limpar filtros</Button>}

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={todosDaPagina} onCheckedChange={togglePagina} disabled={pendentesVisiveis.length === 0} aria-label="Selecionar pendentes da página" />
              </TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Matrícula</TableHead>
              <TableHead>Funcionário</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Detalhe</TableHead>
              <TableHead>Justificativa</TableHead>
              {aba === "historico" && <TableHead>Situação</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={colunas} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : pag.itensPaginados.length === 0 ? (
              <TableRow><TableCell colSpan={colunas} className="text-center text-muted-foreground">
                {aba === "pendentes" ? "Nenhuma justificativa pendente." : "Nenhum ajuste no filtro."}
              </TableCell></TableRow>
            ) : pag.itensPaginados.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  {a.iStatusAprovacao === 0 && <Checkbox checked={sel.has(a.id)} onCheckedChange={() => toggle(a.id)} />}
                </TableCell>
                <TableCell className="whitespace-nowrap">{dataBr(a.dtReferencia)}</TableCell>
                <TableCell>{a.funcionarioMatricula || "—"}</TableCell>
                <TableCell>{a.funcionarioNome || "(funcionário excluído)"}</TableCell>
                <TableCell><Badge variant="secondary">{a.tipoTexto}</Badge></TableCell>
                <TableCell className="whitespace-nowrap">{detalhe(a)}</TableCell>
                <TableCell className="max-w-xs truncate" title={a.sJustificativa}>{a.sJustificativa || "—"}</TableCell>
                {aba === "historico" && <TableCell>{statusBadge(a)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <PaginacaoRodape {...pag} onPaginaChange={pag.setPagina} onTamanhoPaginaChange={pag.setTamanhoPagina} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo ajuste</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 col-span-2">
              <Label>Funcionário</Label>
              <FuncionarioCombobox value={form.IdFuncionario} onChange={(id) => setForm({ ...form, IdFuncionario: id, IdPontoRegistroAfetado: 0 })} />
            </div>
            <div className="space-y-1">
              <Label>Data</Label>
              <Input type="date" value={form.DtReferencia} onChange={(e) => setForm({ ...form, DtReferencia: e.target.value, IdPontoRegistroAfetado: 0 })} />
            </div>
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={String(form.ITipoAjuste)} onValueChange={(v) => setForm({ ...form, ITipoAjuste: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t.v} value={String(t.v)}>{t.t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.ITipoAjuste === 1 && (
              <div className="space-y-1">
                <Label>Hora da batida a incluir</Label>
                <Input type="time" value={form.Hora} onChange={(e) => setForm({ ...form, Hora: e.target.value })} />
              </div>
            )}
            {form.ITipoAjuste === 5 && (
              <div className="space-y-1 col-span-2">
                <Label>Batida a desconsiderar</Label>
                {!form.IdFuncionario ? (
                  <p className="text-sm text-muted-foreground">Escolha o funcionário e a data.</p>
                ) : carregandoBatidas ? (
                  <p className="text-sm text-muted-foreground">Carregando batidas…</p>
                ) : batidasDia.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma batida nesse dia.</p>
                ) : (
                  <Select value={form.IdPontoRegistroAfetado ? String(form.IdPontoRegistroAfetado) : ""}
                    onValueChange={(v) => setForm({ ...form, IdPontoRegistroAfetado: Number(v) })}>
                    <SelectTrigger><SelectValue placeholder="Selecione a batida…" /></SelectTrigger>
                    <SelectContent>
                      {batidasDia.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>{hora(b.dtHoraRegistro)} — {b.tipoBatidaTexto}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            {[2, 3, 4].includes(form.ITipoAjuste) && (
              <div className="space-y-1">
                <Label>Minutos (± no banco de horas)</Label>
                <Input type="number" value={form.FMinutosAjuste} onChange={(e) => setForm({ ...form, FMinutosAjuste: Number(e.target.value) || 0 })} />
                {form.FMinutosAjuste !== 0 && <p className="text-xs text-muted-foreground">= {hm(form.FMinutosAjuste)}</p>}
              </div>
            )}
            <div className="space-y-1 col-span-2">
              <Label>Justificativa</Label>
              <Input value={form.SJustificativa} maxLength={300} onChange={(e) => setForm({ ...form, SJustificativa: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvarNovo}>Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
